import React from 'react';
import { Search, X, Filter } from 'lucide-react';
import { Input } from '../../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { PROJECT_TYPES, STATUS_TYPES } from '../../constants/filterConstants';
import { Button } from '../../../../components/ui/button';

interface ProjectFilters {
  status: string;
  type: string;
  customer: string;
}

interface ProjectFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filters: ProjectFilters;
  onFilterChange: (key: keyof ProjectFilters) => (value: string) => void;
  uniqueCustomers: string[];
  hasActiveFilters: boolean;
  filteredCount: number;
  onResetFilters: () => void;
}

export const ProjectFilters: React.FC<ProjectFiltersProps> = ({
  searchQuery,
  onSearchChange,
  filters,
  onFilterChange,
  uniqueCustomers,
  hasActiveFilters,
  filteredCount,
  onResetFilters,
}) => {
  return (
    <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4 h-8">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="font-medium">Filters</span>
        </div>
        <div className="h-8 flex items-center">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetFilters}
              className="text-gray-500 hover:text-gray-700 transition-opacity duration-200"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </div>
      
      {/* Single Row Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="w-[180px]">
          <Select value={filters.status} onValueChange={onFilterChange('status')}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_TYPES.map(status => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[180px]">
          <Select value={filters.type} onValueChange={onFilterChange('type')}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter by Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {PROJECT_TYPES.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[180px]">
          <Select value={filters.customer} onValueChange={onFilterChange('customer')}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter by Customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {uniqueCustomers.map(customer => (
                <SelectItem key={customer} value={customer}>{customer}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}; 